import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {AfterViewInit, Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {NgForm} from '@angular/forms';
import {UsersService} from '../../../shared/services/users.service';
import {Principal} from '../../../core/auth/principal.service';
import {GlobalSpinnerService} from '../../../core/services/global-spinner.service';
import {SnackBarService} from '../../../core/services/snackbar.service';
import {ServerErrorUtils} from '../../../shared/utils/server-error-utils';
import {User} from '../../../core/auth/user.model';
import {AssignableUserRoleKL, UserRole} from '../../../core/auth/user-role.model';
import {Publisher, PublisherStatus} from '../../../shared/domain/publisher.model';
import {ContentOwnerService} from '../../../shared/services/content-owner.service';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { RolePermission } from 'src/app/core/auth/rbac/role-permission.service';
import { PrincipalUtils } from 'src/app/core/auth/principal.utils';

@Component({
    selector: 'ctv-user-editor-dialog',
    templateUrl: './user-editor-dialog.component.html',
    styleUrls: ['./user-editor-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserEditorDialogComponent implements OnInit, AfterViewInit {
  public readonly RolePermission = RolePermission;
  isEdit: boolean;
  user = new User();
  userRoleKL = AssignableUserRoleKL();
  userRole = UserRole;
  publisherStatuses = [PublisherStatus.ACTIVE];
  userType: {type: 'co' | 'publisher'; id: string};
  disabled = false;
  networkId: string;
  eventsSubjectNetwork = new Subject<string[]>();
  clearFilterItems: Subject<void> = new Subject<void>();
  hidePassword = true;
  readOnly = false;
  @ViewChild('f') userForm: NgForm;
  readonly passwordPattern = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*"-]).{8,}$';

  constructor(public dialogRef: MatDialogRef<UserEditorDialogComponent>,
              private service: UsersService,
              private principal: Principal,
              private route: ActivatedRoute,
              private gss: GlobalSpinnerService,
              private snackBarService: SnackBarService,
              private coService: ContentOwnerService,
              @Inject(MAT_DIALOG_DATA) public data: any) {
    this.user = new User();
    if (data && data.userType) {
      this.userType = data.userType;
      this.userType.type === 'co' ? this.user.contentOwnerIds = [this.userType.id] : this.user.publisherIds = [this.userType.id];
      if (this.userType.type === 'co') {
        this.getContentOwnerDetail();
      }
      // CONTENT_OWNER is no longer a real backend role (folded into USER, 2026-08-02 RBAC
      // reconciliation) - a content-owner-portal account is now a USER with contentOwnerIds set,
      // the same pattern PUBLISHER already uses via publisherIds.
      this.user.roles = [this.userType.type && this.userType.type === 'co' ? this.userRole.USER : this.userRole.PUBLISHER];
      this.disabled = true;
    }
    if (data && data.user) {
      this.isEdit = !!data.user;
      this.user = data.user;
    }
    if (data && data.publisherId) {
      this.user.publisherIds = data.publisherId ? [data.publisherId] : [];
    }

    if (data && data.networkId) {
      this.networkId = data.networkId;
      this.eventsSubjectNetwork.next([this.networkId]);
    }
    this.readOnly = !!data?.readOnly;
  }

  ngOnInit(): void {
    if (this.networkId) {
      // Legacy exclusion list was [CONTENT_OWNER, READ_ONLY_ADMIN, CAMPAIGN_MANAGER, REV_OPS];
      // the first three no longer exist as backend roles (folded into USER, see user-role.model.ts).
      // READ_ONLY_ADMIN is the one unambiguous carry-over. USER/READONLY_USER weren't excluded
      // before under any of their legacy names, so they're left assignable here.
      this.userRoleKL = this.userRoleKL.filter(item => ![UserRole.READ_ONLY_ADMIN].includes(item.key));
    }
  }

  ngAfterViewInit(): void {
    this.dialogRef.updateSize('500px');
    if (this.readOnly) {
      this.userForm?.form.disable({ emitEvent: false });
    }
  }

  isPublisher(){
    return this.user.roles && (this.user.roles.includes(this.userRole.PUBLISHER));
  }

  needsAdvertiserAssignment(): boolean {
    return !!this.user.roles?.includes(this.userRole.ADVERTISER)
      || !!this.user.roles?.includes(this.userRole.FINANCE);
  }

  requiresAdvertiserIds(): boolean {
    return !!this.user.roles?.includes(this.userRole.ADVERTISER);
  }

  createUser() {
    this.gss.showLoader();
    this.user.name = this.user.name?.trim();
    this.user.email = this.user.email?.trim();

    if (!this.user.roles) {
      this.user.roles = [];
    }

    if (!(this.user.roles.includes(UserRole.PUBLISHER) || this.user.roles.includes(UserRole.READONLY_USER))) {
      this.user.publisherIds = [];
    }
    // CONTENT_OWNER is no longer a real role to check against (see above) - contentOwnerIds is
    // now the only signal that this is a content-owner-portal account, whether we're creating one
    // (userType.type === 'co') or editing one that already carries ids from before the collapse.
    if (!(this.userType?.type === 'co' || (this.user.contentOwnerIds && this.user.contentOwnerIds.length))) {
      this.user.contentOwnerIds = [];
    }
    if (!this.needsAdvertiserAssignment()) {
      this.user.advertiserIds = [];
    }
    if (this.isEdit) {
      this.service.update(this.user).subscribe(next => {
          this.gss.hideLoader();
          this.dialogRef.close(true);
          this.snackBarService.success('User updated successfully');
        },
        (e) => {
          const messages = ServerErrorUtils.getValidationMessages(e);
          if (messages) {
            messages.forEach(m => this.snackBarService.error(m));
          } else {
            this.snackBarService.error('Error occurred during user creation');
          }
          this.gss.hideLoader();
        }
      );
    } else {
      if (this.networkId) {
        this.createNetworkUser();
      } else {
        this.createNormalUser();
      }
    }
  }

  createNetworkUser() {
    this.service.createNetwork(this.user, this.networkId).subscribe(next => {
      this.gss.hideLoader();
      this.dialogRef.close(true);
      this.snackBarService.success('User created successfully');
    },
    (e) => {
      const messages = ServerErrorUtils.getValidationMessages(e);
      if (messages) {
        messages.forEach(m => this.snackBarService.error(m));
      } else {
        this.snackBarService.error('Error occurred during user creation');
      }
      this.gss.hideLoader();
    }
    );
  }

  createNormalUser() {
    this.service.create(this.user).subscribe(next => {
      this.gss.hideLoader();
      this.dialogRef.close(true);
      this.snackBarService.success('User created successfully');
    },
    (e) => {
      const messages = ServerErrorUtils.getValidationMessages(e);
      if (messages) {
        messages.forEach(m => this.snackBarService.error(m));
      } else {
        this.snackBarService.error('Error occurred during user creation');
      }
      this.gss.hideLoader();
    }
    );
  }

  onPublisherSelection(pubs: Publisher[]) {
    this.user.publisherIds = pubs.map(p => p.id);
  }

  onContentOwnerSelection(contentOwnerIds: string[]) {
    this.user.contentOwnerIds = contentOwnerIds;
  }

  onAdvertiserSelection(advertisers: { id: string }[]) {
    this.user.advertiserIds = advertisers.map(a => a.id);
  }

  getContentOwnerDetail() {
    this.coService.getCoDetails(this.userType.id).subscribe(
      response => {
        this.user.contentOwners = [response];
      },
      error => {
        ServerErrorUtils.serverValidationOrMsg(error, this.snackBarService, 'Error occurred fetching content owner');
      }
    );
  }

  moreThanOneMainRoleSelected(): boolean {
    return PrincipalUtils.hasMoreThanOneMainRole(this.user);
  }

  generatePassword() {
    const lowers = 'abcdefghijklmnopqrstuvwxyz';
    const uppers = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const specials = '!@#$%^&*"-';
    const all = lowers + uppers + digits + specials;
    const pick = (set: string) => set.charAt(Math.floor(Math.random() * set.length));
    const chars = [pick(lowers), pick(uppers), pick(digits), pick(specials)];
    for (let i = 0; i < 8; i++) {
      chars.push(pick(all));
    }
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    this.user.password = chars.join('');
    this.hidePassword = false;
  }
}
